import { getJsonTokenPropertyName, isJsonObject, type JsonToken } from "./dat-snapshot-json";

export type SnapshotQuickJumpDestinationKind = "category" | "property";

export interface SnapshotQuickJumpDestination {
  context: string;
  kind: SnapshotQuickJumpDestinationKind;
  label: string;
  rawKey: string;
  start: number;
}

const ACRONYM_LABELS: Record<string, string> = {
  api: "API",
  co2: "CO₂",
  dat: "DAT",
  hp: "HP",
  id: "ID",
  json: "JSON",
  kba: "KBA",
  kw: "kW",
  uuid: "UUID",
  vat: "VAT",
  vin: "VIN",
  xml: "XML",
};

const ENGINE_DISPLACEMENT_KEYS = new Set([
  "enginecapacity",
  "enginedisplacement",
  "enginedisplacementccm",
  "enginesize",
]);
const ENGINE_DISPLACEMENT_CONTEXT_KEYS = new Set(["engine", "techinfo", "vehicle"]);
const STRUCTURAL_CATEGORY_KEYS = new Set(["body", "envelope", "vxs", "xml"]);

function getLocalJsonKey(key: string): string {
  const namespaceSeparator = key.lastIndexOf(":");
  return namespaceSeparator === -1 ? key : key.slice(namespaceSeparator + 1);
}

function normalizeJsonKey(key: string): string {
  return getLocalJsonKey(key)
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}

function isStructuralCategoryKey(key: string): boolean {
  const normalizedKey = normalizeJsonKey(key);
  return STRUCTURAL_CATEGORY_KEYS.has(normalizedKey) || normalizedKey.endsWith("response");
}

function humanizeJsonKey(key: string): string {
  const words = getLocalJsonKey(key)
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return words
    .map((word) => {
      const acronym = ACRONYM_LABELS[word.toLowerCase()];

      if (acronym) {
        return acronym;
      }

      if (/^[A-Z\d]+$/.test(word)) {
        return word;
      }

      return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    })
    .join(" ");
}

function getDestinationLabel(key: string, parentPath: string[]): string {
  const normalizedKey = normalizeJsonKey(key);
  const isEngineDisplacement =
    ENGINE_DISPLACEMENT_KEYS.has(normalizedKey) ||
    (normalizedKey === "capacity" &&
      parentPath.some((pathKey) => ENGINE_DISPLACEMENT_CONTEXT_KEYS.has(normalizeJsonKey(pathKey))));

  return isEngineDisplacement ? "Engine Displacement" : humanizeJsonKey(key);
}

function getDestinationContext(parentPath: string[]): string {
  const contextLabels = parentPath
    .filter((key) => !isStructuralCategoryKey(key))
    .map(humanizeJsonKey)
    .filter((label, index, labels) => label && label !== labels[index - 1]);

  return contextLabels.slice(-3).join(" › ");
}

function hasMeaningfulValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (isJsonObject(value)) {
    return Object.keys(value).length > 0;
  }

  return true;
}

function isDatValueWrapper(value: unknown): value is Record<string, unknown> {
  return (
    isJsonObject(value) &&
    Object.hasOwn(value, "value") &&
    Object.values(value).every((nestedValue) => !isJsonObject(nestedValue) && !Array.isArray(nestedValue))
  );
}

function getDestinationKind(value: unknown): SnapshotQuickJumpDestinationKind {
  if (isDatValueWrapper(value)) {
    return "property";
  }

  if (Array.isArray(value)) {
    return value.some((item) => isJsonObject(item) || Array.isArray(item)) ? "category" : "property";
  }

  return isJsonObject(value) ? "category" : "property";
}

function isUsefulDestination(key: string, kind: SnapshotQuickJumpDestinationKind): boolean {
  const normalizedKey = normalizeJsonKey(key);

  if (!normalizedKey || key.toLowerCase().startsWith("xmlns")) {
    return false;
  }

  if (kind === "category") {
    return !isStructuralCategoryKey(key);
  }

  return normalizedKey !== "origin" && normalizedKey !== "value";
}

export function buildSnapshotQuickJumpDestinations(
  serializedSnapshot: string,
  propertyTokens: Pick<JsonToken, "kind" | "start" | "text">[],
): SnapshotQuickJumpDestination[] {
  if (!serializedSnapshot || propertyTokens.length === 0) {
    return [];
  }

  let snapshot: unknown;

  try {
    snapshot = JSON.parse(serializedSnapshot) as unknown;
  } catch {
    return [];
  }

  const destinations: SnapshotQuickJumpDestination[] = [];
  const seenDestinations = new Set<string>();
  let propertyTokenIndex = 0;

  const visit = (value: unknown, path: string[], suppressDestinations = false): void => {
    if (Array.isArray(value)) {
      value.forEach((item) => visit(item, path, suppressDestinations));
      return;
    }

    if (!isJsonObject(value)) {
      return;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      const propertyToken = propertyTokens[propertyTokenIndex];
      propertyTokenIndex += 1;

      const kind = getDestinationKind(nestedValue);
      const parentPath = path;
      const nextPath = [...path, key];
      const label = getDestinationLabel(key, parentPath);
      const deduplicationKey = `${kind}:${label.toLowerCase()}`;
      const isXmlDeclaration = key.startsWith("?") && normalizeJsonKey(key) === "xml";

      if (
        !suppressDestinations &&
        propertyToken &&
        getJsonTokenPropertyName(propertyToken) === key &&
        hasMeaningfulValue(isDatValueWrapper(nestedValue) ? nestedValue.value : nestedValue) &&
        isUsefulDestination(key, kind) &&
        !seenDestinations.has(deduplicationKey)
      ) {
        seenDestinations.add(deduplicationKey);
        destinations.push({
          context: getDestinationContext(parentPath),
          kind,
          label,
          rawKey: key,
          start: propertyToken.start,
        });
      }

      visit(nestedValue, nextPath, suppressDestinations || isDatValueWrapper(nestedValue) || isXmlDeclaration);
    });
  };

  visit(snapshot, []);
  return destinations;
}
